import * as XLSX from 'xlsx';

/**
 * Generic Excel parsing result containing headers and transformed data rows
 */
export interface ExcelParseResult<T> {
    headers: string[];
    data: T[];
    rawData: any[][];
}

/**
 * Converter function type to transform raw Excel row data to target type
 */
export type ExcelRowConverter<T> = (rowData: any[], headers: string[], rowIndex: number) => T;

/**
 * Options for Excel parsing configuration
 */
export interface ExcelParseOptions {
    sheetIndex?: number; // Which sheet to parse (default: 0)
    headerRowIndex?: number; // Which row contains headers (default: 0)
    dataStartRowIndex?: number; // Which row data starts from (default: 1)
    defaultValue?: any; // Default value incase of undefined/null cells.
}

/**
 * Reads and parses an Excel file from an array buffer
 * 
 * @param arrayBuffer - The Excel file data as array buffer
 * @param converter - Function to convert raw row data to target type T
 * @param options - Optional parsing configuration
 * @returns ExcelParseResult containing headers and converted data
 */
export function parseExcelFromArrayBuffer<T>(
    arrayBuffer: ArrayBuffer,
    converter: ExcelRowConverter<T>,
    options: ExcelParseOptions = {}
): ExcelParseResult<T> {
    
    const {
        sheetIndex = 0,
        headerRowIndex = 0,
        dataStartRowIndex = 1,
        defaultValue
    } = options;


    // Parse the Excel file using xlsx
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        throw new Error('No worksheets found in Excel file');
    }

    if (sheetIndex >= workbook.SheetNames.length) {
        throw new Error(`Sheet index ${sheetIndex} is out of range. File has ${workbook.SheetNames.length} sheets.`);
    }

    // Get the specified worksheet
    const sheetName = workbook.SheetNames[sheetIndex];
    const worksheet = workbook.Sheets[sheetName];

    // Convert to JSON array format (array of arrays)
    const jsonData = XLSX.utils.sheet_to_json<any[]>(worksheet, { 
        header: 1,
        defval: defaultValue
    });

    if (jsonData.length === 0) {
        return {
            headers: [],
            data: [],
            rawData: []
        };
    }

    // Extract headers from specified row
    if (headerRowIndex >= jsonData.length) {
        throw new Error(`Header row index ${headerRowIndex} is out of range. Data has ${jsonData.length} rows.`);
    }

    const headers = (jsonData[headerRowIndex] as any[]).map((header, index) => 
        header != null ? String(header) : `Column ${index + 1}`
    );

    // Extract data rows starting from specified index
    const dataRows = jsonData.slice(dataStartRowIndex);


    // Convert data rows using the provided converter function
    const convertedData: T[] = dataRows.map((row, index) => 
        converter(row, headers, index)
    );

    return {
        headers,
        data: convertedData,
        rawData: dataRows
    };
}

/**
 * Default converter function that creates objects with column-based keys
 * This matches the legacy behavior for backward compatibility
 */
export const defaultExcelRowConverter: ExcelRowConverter<{ [key: string]: any }> = (
    rowData: any[], 
    headers: string[],
    rowIndex: number
) => {
    const obj: { [key: string]: any } = {};
    headers.forEach((header, index) => {
        obj[`col_${index}`] = rowData[index];
    });
    return obj;
};
